import { styled } from '@grupoboticario/flora-react-system'
import { FloraButton } from '@grupoboticario/flora-react-button'

export const StyledInputWrapper = styled('div', {
  position: 'relative',
  width: '100%',
})

export const StyledInput = styled('input', {
  display: 'block',
  width: '100%',
  marginTop: '0',
  borderRadius: '$small',
  fontSize: '$bodyLarge',
  fontWeight: '$regular',
  lineHeight: '$shorter',
  gap: '$2',
  border: '$borderWidths$hairline solid transparent',
  boxShadow: '0 0 0 0.3125rem transparent',
  outlineStyle: 'solid',
  outlineWidth: '0.125rem',
  outlineOffset: '-0.125rem',
  outlineColor: 'transparent',
  resize: 'vertical',

  '&:not(:disabled):not([aria-disabled="true"])': {
    transitionDuration: '300ms',
    transitionTimingFunction: 'ease-out',
    transitionProperty: 'border-color, box-shadow, outline-color',
  },

  '&:disabled, &[aria-disabled="true"]': {
    '&, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
      pointerEvents: 'none',
      cursor: 'not-allowed',
      color: '$disabledContent',
      backgroundColor: '$disabledBackground',
      borderColor: '$nonInteractiveOutline',
      boxShadow: 'none',
    },

    '&:not(:focus-visible)': {
      outlineColor: 'transparent',
    },

    '&:hover:not(:focus)': {
      outline: 'none',
    },
  },

  variants: {
    /**
     * Opcional. Especifica o estilo visual do input de acordo com o status
     */
    status: {
      neutral: {},
      success: {},
      error: {},
      warning: {},
      loading: {},
    },

    /**
     * Opcional. Espeficia se o background do input será preenchido ou transparente
     */
    backgroundStyle: {
      default: {},
      transparent: {
        backgroundColor: 'transparent',
      },
    },

    /**
     * Opcional. Adapta cores do input para aplicações em superfícies light ou dark
     */
    surfaceColor: {
      light: {
        backgroundColor: '$backgroundPrimary',
        color: '$nonInteractivePredominant',

        '&:-webkit-autofill': {
          'background-clip': 'text !important',
          '-webkit-text-fill-color': '$colors$nonInteractivePredominant !important',
        },

        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
            color: '$disabledContent',
            backgroundColor: '$disabledBackground',
            borderColor: '$nonInteractiveOutline',
            outline: 'none',
          },
        },
      },
      dark: {
        backgroundColor: '$backgroundAltPrimary',
        color: '$nonInteractiveAltPredominant',

        '&:-webkit-autofill': {
          'background-clip': 'text !important',
          '-webkit-text-fill-color': '$colors$nonInteractiveAltPredominant !important',
        },

        '&:disabled, &[aria-disabled="true"]': {
          '&, &:hover, &:focus-visible, &:hover:not(:focus-visible)': {
            color: '$disabledAltContent',
            backgroundColor: '$disabledAltBackground',
            borderColor: '$nonInteractiveAltOutline',
            outline: 'none',
          },
        },
      },
    },

    /**
     * Opcional. Define o posicionalmento da label em relação ao input
     */
    labelPosition: {
      internal: {
        paddingTop: 'calc($8 - $borderWidths$hairline)',
        paddingBottom: 'calc($3 - $borderWidths$hairline)',
        paddingLeft: 'calc($4 - $borderWidths$hairline)',
        paddingRight: 'calc($4 - $borderWidths$hairline)',

        '&::placeholder': {
          color: 'transparent',
        },
      },
      external: {
        paddingTop: '0.8125rem',
        paddingBottom: '0.8125rem',
        paddingLeft: 'calc($3 - $borderWidths$hairline)',
        paddingRight: 'calc($3 - $borderWidths$hairline)',
      },
    },

    hasLeftIcon: {
      true: {},
    },

    hasRightIcon: {
      true: {},
    },

    hasButton: {
      true: {},
    },

    clicked: {
      true: {
        '&:focus-visible': {
          outlineOffset: '-0.125rem',
        },
      },
      false: {
        '&:focus-visible': {
          outlineOffset: '0.125rem',
        },
      },
    },
  },
  compoundVariants: [
    {
      labelPosition: 'external',
      hasLeftIcon: true,
      css: {
        paddingLeft: '$11',
      },
    },
    {
      labelPosition: 'external',
      hasRightIcon: true,
      css: {
        paddingRight: '$11',
      },
    },
    {
      labelPosition: 'internal',
      hasLeftIcon: true,
      css: {
        paddingLeft: '$12',
      },
    },
    {
      labelPosition: 'internal',
      hasRightIcon: true,
      css: {
        paddingRight: '$12',
      },
    },

    {
      labelPosition: 'external',
      surfaceColor: 'light',
      css: {
        '&::placeholder': {
          color: '$nonInteractiveAuxiliar',
        },
      },
    },
    {
      labelPosition: 'external',
      surfaceColor: 'dark',
      css: {
        '&::placeholder': {
          color: '$nonInteractiveAltAuxiliar',
        },
      },
    },

    {
      clicked: false,
      surfaceColor: 'light',
      css: {
        '&:focus-visible': {
          outlineColor: '$keyboardFocusDefault',
        },
      },
    },
    {
      clicked: false,
      surfaceColor: 'dark',
      css: {
        '&:focus-visible': {
          outlineColor: '$keyboardFocusAlt',
        },
      },
    },
    {
      clicked: true,
      status: 'neutral',
      surfaceColor: 'light',
      css: {
        '&:focus-visible': {
          outlineColor: '$actionableDefault',
        },
      },
    },
    {
      clicked: true,
      status: 'neutral',
      surfaceColor: 'dark',
      css: {
        '&:focus-visible': {
          outlineColor: '$actionableAlt',
        },
      },
    },

    {
      status: 'neutral',
      surfaceColor: 'light',
      css: {
        borderColor: '$nonInteractiveOutline',

        '&:hover:not(:focus-visible)': {
          borderColor: '$actionableDefault',
          outlineColor: '$actionableDefault',
          boxShadow: '0 0 0 0.3125rem $colors$nonPrimaryButtonHover',
        },
      },
    },
    {
      status: 'success',
      surfaceColor: 'light',
      hasButton: false,
      css: {
        borderColor: '$statusSuccessActionableDefault',
        outlineColor: '$statusSuccessActionableDefault',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusSuccessActionableDefault',
          boxShadow: '0 0 0 0.3125rem $colors$statusSuccessNonPrimaryButtonHover',
        },
      },
    },
    {
      status: 'warning',
      surfaceColor: 'light',
      hasButton: false,
      css: {
        borderColor: '$statusAlertActionableDefault',
        outlineColor: '$statusAlertActionableDefault',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusAlertActionableDefault',
          boxShadow: '0 0 0 0.3125rem $colors$statusAlertNonPrimaryButtonHover',
        },
      },
    },
    {
      status: 'error',
      surfaceColor: 'light',
      hasButton: false,
      css: {
        borderColor: '$statusErrorActionableDefault',
        outlineColor: '$statusErrorActionableDefault',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusErrorActionableDefault',
          boxShadow: '0 0 0 0.3125rem $colors$statusErrorNonPrimaryButtonHover',
        },
      },
    },
    {
      status: 'loading',
      surfaceColor: 'light',
      hasButton: false,
      css: {
        borderColor: '$nonInteractiveOutline',
      },
    },

    {
      status: 'neutral',
      surfaceColor: 'dark',
      css: {
        borderColor: '$nonInteractiveAltOutline',

        '&:hover:not(:focus-visible)': {
          borderColor: '$actionableAlt',
          outlineColor: '$actionableAlt',
          boxShadow: '0 0 0 0.3125rem $colors$nonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'success',
      surfaceColor: 'dark',
      css: {
        borderColor: '$statusSuccessActionableAlt',
        outlineColor: '$statusSuccessActionableAlt',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusSuccessActionableAlt',
          boxShadow: '0 0 0 0.3125rem $colors$statusSuccessNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'warning',
      surfaceColor: 'dark',
      css: {
        borderColor: '$statusAlertActionableAlt',
        outlineColor: '$statusAlertActionableAlt',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusAlertActionableAlt',
          boxShadow: '0 0 0 0.3125rem $colors$statusAlertNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'error',
      surfaceColor: 'dark',
      css: {
        borderColor: '$statusErrorActionableAlt',
        outlineColor: '$statusErrorActionableAlt',

        '&:hover:not(:focus-visible)': {
          borderColor: '$statusErrorActionableAlt',
          boxShadow: '0 0 0 0.3125rem $colors$statusErrorNonPrimaryButtonAltHover',
        },
      },
    },
    {
      status: 'loading',
      surfaceColor: 'dark',
      css: {
        borderColor: '$nonInteractiveAltOutline',
      },
    },
  ],
})

export const MessagesWrapper = styled('div', {
  fontFamily: 'inherit',
  display: 'flex',
  gap: '$1',
})

export const InputIcon = styled('div', {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
})

export const InputRightIcon = styled(InputIcon, {
  right: '$4',

  variants: {
    surfaceColor: {
      light: {
        color: '$nonInteractiveAuxiliar',
      },
      dark: {
        color: '$actionableAlt',
      },
    },
  },
})

export const InputLeftIcon = styled(InputIcon, {
  left: '$4',

  variants: {
    surfaceColor: {
      light: {
        color: '$nonInteractiveAuxiliar',
      },
      dark: {
        color: '$nonInteractiveAltEmphasis',
      },
    },
  },
})

export const InputButton = styled(FloraButton, {
  '&.flora--input-button': {
    height: 'auto',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    position: 'relative',
    zIndex: 2,

    svg: {
      width: '$6',
      height: '$6',
    },
  },

  variants: {
    hasIcon: {
      true: {
        '&.flora--input-button': {
          padding: '0 calc($5 - $borderWidths$hairline)',
        },
      },
      false: {
        '&.flora--input-button': {
          padding: '0 calc($3 - $borderWidths$hairline)',
        },
      },
    },
  },
})

export const InputButtonWrapper = styled('div', {
  variants: {
    hasButton: {
      true: {
        width: '100%',
        display: 'flex',
        borderRadius: '$small',

        [`& ${StyledInput}`]: {
          width: '100%',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
    },
  },
})
